import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { List, Typography, Grid, Anchor } from 'components/base';
import { CloudinaryPicture } from 'components/rich';
import useDataContext from 'data/context/data';
import productCoverImage from 'utils/productCoverImage';
import BaseNav from 'components/layout/nav';
import Icon, { ICON_TYPE } from 'components/icon';
import { NAV_ITEMS } from '../../specifications/lists';

import {
  CategoryPageLink,
  ShopPageLink,
  HomePageLink,
  UserPageLink,
  SupportPageLink,
  ProductDetailLink,
  getProductDetailUrl,
} from '../pageLinks';

const Nav = props => {
  const dataContext = useDataContext();

  const items = useMemo(
    () =>
      NAV_ITEMS.reduce((acc, item) => {
        const category = dataContext.getCategory(item);
        if (category) {
          acc.push({ category });
          return acc;
        }

        // Dropdown links are in every page's SSR HTML — skip products
        // without a specification url so ProductDetailLink's /{productId}
        // 404 fallback never reaches crawlers.
        const product = dataContext.getProduct(item);
        if (product && getProductDetailUrl(product.id)) {
          acc.push({ product });
          return acc;
        }

        return acc;
      }, []),
    [dataContext],
  );

  return (
    <BaseNav topPosition={props.topPosition} expanded={props.expanded}>
      <List className="navbar-nav">
        <List.Item className="nav-item">
          <HomePageLink>
            <Anchor className="nav-link">HOME</Anchor>
          </HomePageLink>
        </List.Item>

        <ProductsItem items={items} />

        <List.Item className="nav-item">
          <UserPageLink section="creations">
            <Anchor className="nav-link">MY PROJECTS</Anchor>
          </UserPageLink>
        </List.Item>

        <List.Item className="nav-item">
          <SupportPageLink>
            <Anchor className="nav-link">SUPPORT</Anchor>
          </SupportPageLink>
        </List.Item>

        <List.Item className="nav-item">
          <Anchor href="/blog/" className="nav-link">
            BLOG
          </Anchor>
        </List.Item>
      </List>
    </BaseNav>
  );
};

Nav.propTypes = {
  topPosition: PropTypes.number,
  expanded: PropTypes.bool,
};

const ProductsItem = ({ items }) => {
  const hasChild = items.length >= 1;

  const className = classNames({
    'nav-link': true,
    'nav-link-has-child': hasChild,
  });

  return (
    <BaseNav.Item
      hasChild={hasChild}
      link={
        <ShopPageLink>
          <Anchor className={className}>PRODUCTS</Anchor>
        </ShopPageLink>
      }
    >
      {hasChild && (
        <Grid.Row>
          <Grid.Col xs={12} lg={12}>
            {items.map(item => {
              const { category, product } = item;
              if (category) {
                return (
                  <CategoryPageLink key={category.id} category={category}>
                    <Anchor className="header-product">
                      <CloudinaryPicture imageKey={category.image} type="small" loading="lazy" />

                      <div>
                        {/* Nav tile labels are navigation, not content headings — div keeps the h4 styling */}
                        <div className="h4">
                          <Typography.Span>
                            {category.id === 'family-history' && (
                              <>
                                <Icon type={ICON_TYPE.ANCESTRY} className="text-ancestry" />{' '}
                              </>
                            )}
                            {category.id === 'familysearch' && (
                              <>
                                <Icon type={ICON_TYPE.FAMILY_SEARCH} className="text-familysearch" />{' '}
                              </>
                            )}
                            {category.name.toUpperCase()}
                          </Typography.Span>
                        </div>
                        <Typography.P className="small">{category.shortDescription}</Typography.P>
                      </div>
                    </Anchor>
                  </CategoryPageLink>
                );
              }

              if (product) {
                return (
                  <ProductDetailLink key={product.id} productId={product.id}>
                    <Anchor className="header-product">
                      <CloudinaryPicture
                        prefix="products/"
                        imageKey={productCoverImage(product)?.key}
                        type="small"
                        loading="lazy"
                      />
                      <div>
                        <div className="h4">
                          <Typography.Span>{product.name}</Typography.Span>
                        </div>
                        <Typography.P className="small">{product.shortDescription}</Typography.P>
                      </div>
                    </Anchor>
                  </ProductDetailLink>
                );
              }

              return null;
            })}
          </Grid.Col>
        </Grid.Row>
      )}
    </BaseNav.Item>
  );
};

ProductsItem.propTypes = {
  items: PropTypes.array.isRequired,
};

export default Nav;
